import { t } from "@lingui/core/macro"
import { Trans } from "@lingui/react/macro"
import { useQuery, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { FillInWorkbook } from "@/features/list-of-materials/components/fill-in/FillInWorkbook"
import { LomChain } from "@/features/list-of-materials/components/home/LomChain"
import { OpenItemsList } from "@/features/list-of-materials/components/home/OpenItemsList"
import { RecentChanges } from "@/features/list-of-materials/components/home/RecentChanges"
import { HowLink } from "@/features/list-of-materials/components/how-it-works/HowLink"
import { Chip } from "@/features/list-of-materials/components/ui/Chip"
import {
  PageHead,
  PageStatus,
  SectionTitle,
} from "@/features/list-of-materials/components/ui/PageHead"
import { formatCount } from "@/features/list-of-materials/core/format"
import { openItems, statusLine } from "@/features/list-of-materials/core/lomHome"
import { useCanEditLom } from "@/features/list-of-materials/hooks/useCanEditLom"
import {
  lomOverviewQueryOptions,
  lomRecentChangesQueryOptions,
  lomVersionsQueryOptions,
  useSelectedLomVersion,
} from "@/features/list-of-materials/hooks/useLomVersion"

export const Route = createFileRoute("/(authenticated)/list-of-materials/")({
  loaderDeps: ({ search }) => ({ version: search.version }),
  loader: async ({ context, deps }) => {
    const { selected } = await context.queryClient.ensureQueryData(
      lomVersionsQueryOptions(deps.version),
    )
    if (!selected) return
    void context.queryClient.ensureQueryData(lomOverviewQueryOptions(selected.id))
    void context.queryClient.prefetchQuery(lomRecentChangesQueryOptions(selected.id))
  },
  component: LomHomePage,
})

function LomHomePage() {
  const { version, versionSearch } = useSelectedLomVersion()
  const { data: overview } = useSuspenseQuery(lomOverviewQueryOptions(version.id))
  const { data: changes } = useQuery(lomRecentChangesQueryOptions(version.id))

  const canEdit = useCanEditLom()
  const items = openItems(overview)
  const { creme, white, unknown } = overview.stores

  return (
    <>
      <PageHead
        help={<HowLink />}
        title={<Trans>List of Materials</Trans>}
        sub={<Trans>List of Materials settings for every store built with this version.</Trans>}
        right={
          <PageStatus>
            {version.isActive
              ? t`Active version, ${version.versionLabel}`
              : t`Version ${version.versionLabel}`}
          </PageStatus>
        }
      />

      <div className="mb-8 flex flex-wrap items-center gap-4">
        <Chip tone={items.length === 0 ? "ok" : "fill"} dot>
          {statusLine(items)}
        </Chip>
        <span className="text-lom-ink-2 text-base">
          <b className="text-lom-ink font-medium">{t`${formatCount(creme + white + unknown)} stores`}</b>
          {": "}
          {t`${formatCount(creme)} creme (1.0), ${formatCount(white)} white (2.0)`}
          {unknown > 0 && t`, ${formatCount(unknown)} without a version`}
        </span>
      </div>

      <SectionTitle>
        <Trans>The chain, from drawing to list</Trans>
      </SectionTitle>
      <LomChain overview={overview} version={versionSearch.version} />

      <div className="mt-10 grid grid-cols-2 gap-6 max-[900px]:grid-cols-1">
        <div>
          <SectionTitle>
            <Trans>To fill in</Trans>
          </SectionTitle>
          <FillInWorkbook configVersionId={version.id} canEdit={canEdit} />
          <OpenItemsList items={items} version={versionSearch.version} />
        </div>
        {changes !== null && (
          <div>
            <SectionTitle>
              <Trans>Recent changes</Trans>
            </SectionTitle>
            <RecentChanges changes={changes} />
          </div>
        )}
      </div>
    </>
  )
}
